import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { Dynamic } from '@shared/registry/dynamic';

import type { ImageAspectRatio, ImageConfig } from './image.types';

const ASPECT_RATIO_MAP: Record<ImageAspectRatio, { width: number; height: number }> = {
  '16:9': { width: 16, height: 9 },
  '4:3': { width: 4, height: 3 },
  '3:2': { width: 3, height: 2 },
  '1:1': { width: 1, height: 1 },
  '2:3': { width: 2, height: 3 },
  '3:4': { width: 3, height: 4 },
  '9:16': { width: 9, height: 16 },
};

const BORDER_RADIUS_MAP: Record<string, string> = {
  none: 'rounded-none',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};

const OBJECT_FIT_MAP: Record<string, string> = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  none: 'object-none',
};

@Component({
  selector: 'app-image',
  templateUrl: './image.html',
  imports: [NgOptimizedImage, Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Image {
  readonly config = input.required<ImageConfig>();

  private readonly isLoaded = signal(false);

  /** Resolves dimensions from aspectRatio or explicit width/height, defaults to 16:9 */
  protected readonly dimensions = computed(() => {
    const config = this.config();
    if (config.aspectRatio) {
      return ASPECT_RATIO_MAP[config.aspectRatio];
    }
    if (config.width && config.height) {
      return { width: config.width, height: config.height };
    }
    return ASPECT_RATIO_MAP['16:9'];
  });

  protected readonly showSkeleton = computed(() => {
    const skeletonEnabled = this.config().showSkeleton ?? true;
    return skeletonEnabled && !this.isLoaded();
  });

  protected readonly skeletonConfig = computed(() => {
    const dimensions = this.dimensions();
    return {
      height: `${dimensions.height}px`,
      width: `${dimensions.width}px`,
      borderRadius: this.mapBorderRadiusToSkeleton(this.config().borderRadius),
    };
  });

  protected readonly containerClasses = computed(() => {
    const config = this.config();
    const radiusClass = BORDER_RADIUS_MAP[config.borderRadius ?? '2xl'] ?? 'rounded-2xl';
    return `relative overflow-hidden ${radiusClass}`;
  });

  protected readonly imageClasses = computed(() => {
    const config = this.config();
    const objectFitClass = OBJECT_FIT_MAP[config.objectFit ?? 'cover'] ?? 'object-cover';
    const radiusClass = BORDER_RADIUS_MAP[config.borderRadius ?? '2xl'] ?? 'rounded-2xl';
    const opacityClass = this.isLoaded() ? 'opacity-100' : 'opacity-0';
    return `w-full transition-opacity duration-300 ${objectFitClass} ${radiusClass} ${opacityClass}`;
  });

  protected onImageLoad(): void {
    this.isLoaded.set(true);
  }

  private mapBorderRadiusToSkeleton(
    radius?: ImageConfig['borderRadius'],
  ): 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full' {
    switch (radius) {
      case 'none':
        return 'sm';
      case 'md':
        return 'md';
      case 'lg':
        return 'lg';
      case 'xl':
        return 'xl';
      case '2xl':
        return '2xl';
      case 'full':
        return 'full';
      default:
        return '2xl';
    }
  }
}
